import { useState } from 'react';
import { X, AlertCircle, Truck, Store } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface Address {
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
}

interface Props {
  orderId: string;
  orderType?: 'order' | 'bulk_order';
  // Current mode the order is in. Determines the only valid target.
  currentMode: 'pickup' | 'ship';
  // If the order has no shipping address (e.g. was created pickup-only with a
  // pharmacy stub), we require the admin to provide one in this same modal
  // when switching to ship.
  needsShippingAddress?: boolean;
  existingAddress?: Address | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function SwitchDeliveryModeModal({
  orderId,
  orderType = 'order',
  currentMode,
  needsShippingAddress,
  existingAddress,
  onClose,
  onSuccess
}: Props) {
  const targetMode: 'pickup' | 'ship' = currentMode === 'pickup' ? 'ship' : 'pickup';
  const requireAddress = targetMode === 'ship' && (needsShippingAddress || !existingAddress?.street);

  const [reason, setReason] = useState('');
  const [street, setStreet] = useState(existingAddress?.street || '');
  const [street2, setStreet2] = useState(existingAddress?.street2 || '');
  const [city, setCity] = useState(existingAddress?.city || '');
  const [state, setState] = useState(existingAddress?.state || '');
  const [zipCode, setZipCode] = useState(existingAddress?.zipCode || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonOk = reason.trim().length >= 5 && reason.trim().length <= 500;
  const addressOk = !requireAddress || (
    street.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0 &&
    zipCode.trim().length > 0
  );
  const ready = reasonOk && addressOk;

  const handleSubmit = async () => {
    if (!ready) return;
    setSubmitting(true);
    setError(null);

    const shippingAddress = (requireAddress || (targetMode === 'ship' && street.trim()))
      ? {
          street: street.trim(),
          street2: street2.trim() || undefined,
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim()
        }
      : undefined;

    const body = { targetMode, reason: reason.trim(), shippingAddress };

    try {
      const res = orderType === 'bulk_order'
        ? await apiClient.switchBulkOrderDeliveryMode(orderId, body)
        : await apiClient.switchOrderDeliveryMode(orderId, body);

      if (!res.success) {
        const msg =
          res.code === 'patient_ship_not_switchable'
            ? 'Patient-shipped orders cannot be switched to pickup.'
            : res.code === 'pending_action_exists'
              ? 'Another action is already pending on this order — wait for it to resolve.'
              : res.code === 'order_terminal'
                ? 'The order has already left the pharmacy — file a claim instead.'
                : res.code === 'shipping_address_required'
                  ? 'A shipping address is required to switch this order back to ship.'
                  : res.error || 'Failed to switch delivery mode.';
        setError(msg);
        return;
      }
      if (res.pendingPharmacyReview) {
        toast.success('Switch submitted to pharmacy for review.');
      } else if ((res as any).partial) {
        toast.warning(res.message || 'Some sub-orders could not be switched.');
      } else {
        toast.success(res.message || `Switched to ${targetMode}.`);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to switch delivery mode.');
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = targetMode === 'pickup' ? Store : Truck;
  const verb = targetMode === 'pickup' ? 'Switch to pickup' : 'Switch to ship';

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]"
      onClick={onClose}
    >
      <div
        className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-medium text-gray-900">{verb}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          Current mode: <span className="font-medium">{currentMode}</span>
          <span className="mx-1">→</span>
          <span className="font-medium text-indigo-700">{targetMode}</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3 flex gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={targetMode === 'pickup'
                ? 'e.g. Patient prefers to pick up at the pharmacy on Friday.'
                : 'e.g. Patient cannot make it for pickup — ship to home address.'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className={`text-xs mt-1 ${reasonOk ? 'text-gray-500' : 'text-red-600'}`}>
              {reason.trim().length}/500 — 5 character minimum
            </div>
          </div>

          {requireAddress && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2">
              <div className="text-sm text-amber-900 font-medium">
                Shipping address required
              </div>
              <div className="text-xs text-amber-800">
                This order has no shipping address on file. Provide one to complete the switch.
              </div>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Street"
                className="w-full px-2 py-1.5 border border-amber-300 rounded text-sm"
              />
              <input
                type="text"
                value={street2}
                onChange={(e) => setStreet2(e.target.value)}
                placeholder="Unit / Suite (optional)"
                className="w-full px-2 py-1.5 border border-amber-300 rounded text-sm"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="px-2 py-1.5 border border-amber-300 rounded text-sm col-span-1"
                />
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="State"
                  className="px-2 py-1.5 border border-amber-300 rounded text-sm col-span-1"
                  maxLength={2}
                />
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="ZIP"
                  className="px-2 py-1.5 border border-amber-300 rounded text-sm col-span-1"
                />
              </div>
            </div>
          )}

          {!requireAddress && targetMode === 'pickup' && (
            <div className="text-xs text-gray-500">
              Pharmacy will archive any printed shipping label and hold the order for pickup once approved.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !ready}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting…' : verb}
          </button>
        </div>
      </div>
    </div>
  );
}
